import React, { useState } from 'react';
import s from './s.modules.less';
import { Button, message, Modal, Select } from 'antd';
import useGetLatestVisitAllProspects from 'hooks/textVisit/useGetLatestVisitAllProspects';
import useGetLatestVisitTopProspects from 'hooks/textVisit/useGetLatestVisitTopProspects';
import dayjs from 'dayjs';
import { Prospect } from 'types/common';
import usePracticeStateList from 'hooks/usePracticeStateList';
import AddAndUpdateProspects from 'components/AddAndUpdateProspects';
import { openManageSubscriptionModal } from 'utils/globalLayerControl';
import { useRequest } from 'ahooks';
import { getProspectList } from 'api/prospect';

type Props = {
    open?: boolean;
    onClose?: () => void;
    onSelected?: (patient: Prospect) => void;
};

const SelectPatientModal = ({ open, onClose, onSelected }: Props) => {
    const [latestVisitAllProspects, loadingLatestVisitAllProspects, fetchLatestVisitAllProspects] = useGetLatestVisitAllProspects();
    const [latestVisitTopProspects, loadingLatestVisitTopProspects, fetchLatestVisitTopProspects] = useGetLatestVisitTopProspects();
    const [selectedProspectId, setSelectedProspectId] = useState<number | undefined>(undefined);
    const [serverPracticeStateList, serverPracticeStateListLoading, fetchServerPracticeStateList] = usePracticeStateList();
    const [supportStates, setSupportStates] = useState<string[]>([]);
    const [showUnSupportStateModal, setShowUnSupportStateModal] = useState<boolean>(false);
    const [showMissProfileFieldsModal, setShowMissProfileFieldsModal] = useState<boolean>(false);
    const [missFields, setMissFields] = useState<string[]>([]);
    const [showEditClientModal, setShowEditClientModal] = useState<boolean>(false);
    const [currentEditClient, setCurrentEditClient] = useState<Prospect | undefined>(undefined);

    const { data, loading: prospectListLoading, run: fetchProspectList } = useRequest(getProspectList);

    const prospectList: Prospect[] = data?.data?.data || [];

    React.useEffect(() => {
        if (serverPracticeStateList && serverPracticeStateList.length > 0) {
            const _supportState: string[] = [];
            serverPracticeStateList.forEach((item) => {
                if (item.state) {
                    _supportState.push(item.state!);
                }
            });
            setSupportStates(_supportState);
        }
    }, [serverPracticeStateList]);

    const hasRecently = true;

    React.useEffect(() => {
        if (!open) {
            setSelectedProspectId(undefined);
        }
    }, [open]);

    React.useEffect(() => {
        //todo remove
        //onSelected?.(latestVisitTopProspects?.[0]);
    }, [latestVisitTopProspects]);

    const handleSelectProspect = (prospect: Prospect) => {
        //todo
        // onSelected?.(prospect);
        // return;

        if (!prospect?.state || (prospect && !supportStates.includes(prospect.state))) {
            setShowUnSupportStateModal(true);
            return;
        }
        const _missField: string[] = [];
        if (!prospect.address) {
            _missField.push('Address');
        }
        if (!prospect.gender) {
            _missField.push('Gender');
        }
        if (!prospect.city) {
            _missField.push('City');
        }
        if (!prospect.zip) {
            _missField.push('Zip');
        }
        if (!prospect.tel) {
            _missField.push('Phone');
        }
        // if (!prospect.email) {
        //     _missField.push('Email');
        // }
        if (!prospect.birthday) {
            _missField.push('Birthday');
        }
        if (!prospect.firstName) {
            _missField.push('First Name');
        }
        if (!prospect.lastName) {
            _missField.push('Last Name');
        }
        if (!prospect.state) {
            _missField.push('State');
        }
        if (_missField.length > 0) {
            setMissFields(_missField);
            setCurrentEditClient(prospect);
            setShowMissProfileFieldsModal(true);
            return;
        } else {
            setMissFields([]);
        }

        if (prospect) {
            onSelected?.(prospect);
        }
    };

    return (
        <div className={s.out}>
            <Modal
                open={open}
                destroyOnClose
                onCancel={onClose}
                width={600}
                footer={null}
                zIndex={99}
            >
                <div className={s.wrap}>
                    <h4>Search and select a patient</h4>
                    <div className={s.searchWrap}>
                        <Select
                            loading={prospectListLoading}
                            allowClear
                            showSearch
                            filterOption={(input, option) => {
                                const name = option?.children?.join('');
                                if (!name) return true;
                                return name?.toLowerCase().indexOf(input?.toLowerCase()) >= 0;
                            }}
                            size="large"
                            value={selectedProspectId}
                            style={{ width: '100%' }}
                            onChange={(value) => {
                                setSelectedProspectId(value);
                            }}
                            virtual={false}
                        >
                            {
                                prospectList.map((prospect) => (
                                    <Select.Option key={prospect.id} value={prospect.id}>
                                        {prospect.firstName} {prospect.lastName}
                                    </Select.Option>
                                ))
                            }
                        </Select>
                    </div>
                    <Button
                        size="large"
                        type="primary"
                        block
                        className={s.btn}
                        disabled={!selectedProspectId}
                        onClick={() => {
                            const targetProspect = prospectList?.find((prospect) => prospect.id === selectedProspectId);
                            if (targetProspect) {
                                handleSelectProspect(targetProspect);
                            }
                        }}
                    >Confirm
                    </Button>
                    {
                        hasRecently && (
                            <div className={s.recentlyTitle}>
                                Patients you’ve seen recently
                            </div>
                        )
                    }
                    {
                        hasRecently && (
                            <div className={s.recentlyList}>
                                {
                                    latestVisitTopProspects?.map((prospect) => {
                                        const firstLetterOfFirstName = prospect.firstName?.charAt(0);
                                        const firstLetterOfLastName = prospect.lastName?.charAt(0);
                                        const nameIcon = `${firstLetterOfFirstName}${firstLetterOfLastName}`;
                                        return (
                                            <div
                                                onClick={() => {
                                                    handleSelectProspect(prospect);
                                                }}
                                                className={s.recentlyItem}
                                            >
                                                <div className={s.icon}>
                                                    {nameIcon}
                                                </div>
                                                <div className={s.info}>
                                                    <div className={s.nameAndTime}>
                                                        <div className={s.name}>
                                                            {prospect.firstName} {prospect.lastName}
                                                        </div>
                                                        {/* Last seen: March 14, 2024 */}
                                                        <div className={s.time}>
                                                            Last seen: {dayjs(prospect.lastSeenDate).format('MMMM D, YYYY')}
                                                        </div>
                                                    </div>
                                                    <div className={s.lastApp}>
                                                        Last appointment:{prospect.lastAppointmentType}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        )
                    }
                </div>
            </Modal>
            <Modal
                open={showUnSupportStateModal}
                onCancel={() => setShowUnSupportStateModal(false)}
                zIndex={100}
                footer={(
                    <div className={s.btnWrap}>
                        <Button
                            onClick={() => {
                                setShowUnSupportStateModal(false);
                            }}
                            style={{ marginLeft: '12px' }}
                        >Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                openManageSubscriptionModal('7');
                                setShowUnSupportStateModal(false);
                            }}
                            type="primary"
                            className={s.update}
                        >Update practice information
                        </Button>
                    </div>
                )}
                width={620}
            >
                <div className={s.unSupportStateModal}>
                    <div className={s.icon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M11.9995 9V11M11.9995 15H12.0095M5.07134 19H18.9277C20.4673 19 21.4296 17.3333 20.6598 16L13.7316 4C12.9618 2.66667 11.0373 2.66667 10.2675 4L3.33929 16C2.56949 17.3333 3.53174 19 5.07134 19Z" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <div className={s.info}>
                        <div className={s.title}>Can’t proceed with prescription</div>
                        <div className={s.des}>This patient is not associated with any of your practices in their state. Please select another patient or update your practice information to continue.</div>
                    </div>
                </div>
            </Modal>
            <Modal
                open={showMissProfileFieldsModal}
                onCancel={() => setShowMissProfileFieldsModal(false)}
                zIndex={100}
                footer={(
                    <div className={s.btnWrap}>
                        <Button
                            onClick={() => {
                                setShowMissProfileFieldsModal(false);
                            }}
                            style={{ marginLeft: '12px' }}
                        >Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                setShowEditClientModal(true);
                                setShowMissProfileFieldsModal(false);
                            }}
                            type="primary"
                            className={s.update}
                        >
                            Edit profile
                        </Button>
                    </div>
                )}
                width={620}
            >
                <div className={s.unSupportStateModal}>
                    <div className={s.icon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M11.9995 9V11M11.9995 15H12.0095M5.07134 19H18.9277C20.4673 19 21.4296 17.3333 20.6598 16L13.7316 4C12.9618 2.66667 11.0373 2.66667 10.2675 4L3.33929 16C2.56949 17.3333 3.53174 19 5.07134 19Z" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <div className={s.info}>
                        <div className={s.title}>Can’t proceed with prescription</div>
                        <div className={s.des}>
                            <p>This patient is missing the following information:</p>
                            <ul>
                                {
                                    missFields.map((item) => (
                                        <li key={item}>{item}</li>
                                    ))
                                }
                            </ul>
                            <p>To continue with prescribing, please add the information to their profile. </p>
                        </div>
                    </div>
                </div>
            </Modal>
            {
                showEditClientModal && (
                    <AddAndUpdateProspects
                        show={showEditClientModal}
                        onCannel={() => {
                            setShowEditClientModal(false);
                            setCurrentEditClient(undefined);
                        }}
                        onSuccess={() => {
                            setShowEditClientModal(false);
                            fetchProspectList();
                            fetchLatestVisitTopProspects();
                            setCurrentEditClient(undefined);
                        }}
                        item={currentEditClient}
                    />
                )
            }
        </div>
    );
};

export default SelectPatientModal;
